/**
 * Справочник упражнений на клиенте.
 *
 * Нужен, чтобы превратить `exercise_id` в название. Раньше экраны брали
 * названия из сегодняшней тренировки — и «История» за прошлые месяцы
 * рисовала «Упражнение #12» почти в каждой строке.
 *
 * Справочник грузится один раз на запуск и лежит в IndexedDB: в зале без
 * связи названия всё равно должны быть.
 *
 * ПОЧЕМУ ЗДЕСЬ ПОЯВИЛАСЬ ПОДПИСКА. Каталожная часть статична и меняется
 * вместе с деплоем — под неё хватало одной переменной модуля. Личные движения
 * человек заводит прямо во время тренировки, и без оповещения экраны держали
 * бы старый список до перезагрузки вкладки: только что заведённое движение
 * называлось бы «Упражнение #1000001» в «Истории» и не находилось бы поиском
 * на соседнем экране.
 */

import { useEffect, useState } from 'react'

import { api } from './client'
import { readCache, saveCache } from '../offline/db'
import type { Exercise, ExerciseInput } from './types'

const CACHE_KEY = 'exercises'

/** Один запрос на весь запуск: параллельные вызовы делят общий промис. */
let inflight: Promise<Exercise[]> | null = null
let loaded: Exercise[] | null = null

const listeners = new Set<(list: Exercise[]) => void>()

function publish(list: Exercise[]): void {
  loaded = list
  for (const listener of listeners) listener(list)
}

export async function fetchExercises(): Promise<Exercise[]> {
  if (loaded) return loaded
  if (inflight) return inflight

  inflight = (async () => {
    try {
      const list = await api.get<Exercise[]>('/exercises')
      publish(list)
      await saveCache(CACHE_KEY, list)
      return list
    } catch (error) {
      // Нет связи — берём последний известный справочник. Название
      // упражнения не та величина, ради свежести которой стоит оставлять
      // человека с номерами вместо слов.
      const cached = await readCache<Exercise[]>(CACHE_KEY)
      if (cached) {
        publish(cached.value)
        return cached.value
      }
      throw error
    } finally {
      inflight = null
    }
  })()

  return inflight
}

/**
 * Завести своё движение.
 *
 * Сервер идемпотентен по названию: повторная отправка возвращает то же
 * движение, а совпадение с каталожным — каталожное. Поэтому здесь не
 * добавление вслепую, а слияние по id: иначе двойной тап по кнопке нарисовал
 * бы в списке две одинаковые строки, которых в базе нет.
 */
export async function createExercise(input: ExerciseInput): Promise<Exercise> {
  const created = await api.post<Exercise>('/exercises', input)
  merge(created)
  return created
}

/**
 * Вписать движение в справочник, не потеряв остального.
 *
 * `loaded === null` значит «справочник ещё не грузили», а НЕ «справочник
 * пуст». Собрать список из одного созданного движения и записать его в
 * IndexedDB значило бы стереть весь каталог с устройства: экраны «История» и
 * «Цели» начали бы показывать номера вместо названий, и до следующей удачной
 * загрузки это не починилось бы.
 */
function merge(exercise: Exercise): void {
  if (loaded === null) return
  const rest = loaded.filter((e) => e.id !== exercise.id)
  const next = [...rest, exercise]
  publish(next)
  // Промах кеша не должен превращать удачное создание в ошибку: движение
  // уже на сервере, а IndexedDB — только ускорение.
  void saveCache(CACHE_KEY, next).catch(() => undefined)
}

/**
 * Убрать своё движение из выбора.
 *
 * Из справочника оно не пропадает: по нему записаны подходы, и «История»
 * должна и дальше называть их словами. Меняется только пометка.
 */
export async function archiveExercise(id: number): Promise<void> {
  await api.delete<void>(`/exercises/${id}`)
  const known = (loaded ?? []).find((e) => e.id === id)
  if (known) merge({ ...known, is_archived: true })
}

export interface Catalog {
  byId: Map<number, Exercise>
  /** Название или честное «Упражнение #id», если справочник ещё не приехал. */
  name: (id: number) => string
  /** Всё, включая убранное: по нему есть история, и её надо называть. */
  list: Exercise[]
  /** То, из чего можно выбирать сейчас. */
  selectable: Exercise[]
  loading: boolean
}

export function useExercises(): Catalog {
  const [list, setList] = useState<Exercise[]>(loaded ?? [])
  const [loading, setLoading] = useState(loaded === null)

  useEffect(() => {
    let alive = true
    const listener = (next: Exercise[]) => {
      if (alive) setList(next)
    }
    listeners.add(listener)

    // Между первым рендером и этим эффектом мог пройти publish: эффекты
    // React запускает асинхронно, а промис справочника разрешается в
    // микрозадаче. Без этой сверки экран остался бы с пустым списком
    // навсегда — второго publish уже не будет.
    if (loaded && loaded !== list) setList(loaded)

    if (loaded) {
      setLoading(false)
    } else {
      fetchExercises()
        .catch(() => {
          // Экран обязан работать и без справочника: он покажет номера.
        })
        .finally(() => {
          if (alive) setLoading(false)
        })
    }

    return () => {
      alive = false
      listeners.delete(listener)
    }
  }, [])

  const byId = new Map(list.map((e) => [e.id, e]))
  return {
    byId,
    list,
    selectable: list.filter((e) => !e.is_archived),
    loading,
    name: (id: number) => byId.get(id)?.name ?? `Упражнение #${id}`,
  }
}

/** id целевого движения пользователя — жим лёжа со штангой. */
export const BENCH_PRESS_ID = 1
